
import { useState, useEffect } from 'react';
import { Wallet, CheckCircle, AlertCircle } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { useWalletPersistence } from '@/hooks/useWalletPersistence';

interface InitiationWalletProps {
  onComplete: (data: { walletAddress: string; chainType: string }) => void;
}

const InitiationWallet = ({ onComplete }: InitiationWalletProps) => {
  const [address, setAddress] = useState('');
  const [isValid, setIsValid] = useState<boolean | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { persistedWallet, persistWallet, checkWalletInitiation } = useWalletPersistence();

  // Pre-populate with persisted wallet if available
  useEffect(() => {
    if (persistedWallet && !address) {
      setAddress(persistedWallet);
      setIsValid(validateSolanaAddress(persistedWallet));
    }
  }, [persistedWallet, address]);

  const validateSolanaAddress = (addr: string): boolean => {
    if (!addr || addr.length < 32 || addr.length > 44) {
      return false;
    }
    // Basic base58 validation
    const base58Regex = /^[1-9A-HJ-NP-Za-km-z]+$/;
    return base58Regex.test(addr);
  };

  const handleAddressChange = (value: string) => {
    const trimmedValue = value.trim();
    setAddress(trimmedValue);
    
    if (trimmedValue) {
      const valid = validateSolanaAddress(trimmedValue);
      setIsValid(valid);
    } else {
      setIsValid(null);
    }
  };

  const handleSubmit = async () => {
    if (!address.trim()) {
      toast.error('Please enter a wallet address');
      return;
    }
    
    if (!validateSolanaAddress(address.trim())) {
      toast.error('Please enter a valid Solana wallet address');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const walletAddress = address.trim();
      
      // Check if wallet is already initiated
      const existingData = await checkWalletInitiation(walletAddress);
      if (existingData) {
        toast.success(`Welcome back! Found existing BroCode: ${existingData.brocode}`);
      }
      
      // Persist wallet for future sessions
      await persistWallet(walletAddress, 'solana');
      
      console.log('Wallet address submitted:', walletAddress);
      toast.success("Wallet address confirmed!");
      
      onComplete({
        walletAddress: walletAddress,
        chainType: 'solana'
      });
    } catch (error) {
      console.error('Error submitting wallet address:', error);
      toast.error('Failed to confirm wallet address');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePaste = async () => {
    try {
      const text = await navigator.clipboard.readText();
      handleAddressChange(text);
      toast.success('Address pasted from clipboard');
    } catch (error) {
      toast.error('Failed to paste from clipboard');
    }
  };

  return (
    <div className="space-y-6">
      <div className="text-center">
        <Wallet className="mx-auto mb-4 text-tan" size={48} />
        <h3 className="text-xl font-bold text-tan mb-2">Enter Your Solana Wallet Address</h3>
        <p className="text-tan-dark">
          Enter your Solana wallet address to secure your BroCode
        </p>
      </div>

      <Card className="bg-navy-dark border-tan-dark">
        <CardContent className="p-4">
          <div className="space-y-4">
            <div className="space-y-2">
              <div className="relative">
                <Input
                  value={address}
                  onChange={(e) => handleAddressChange(e.target.value)}
                  placeholder="Enter your Solana wallet address (32-44 characters)..."
                  className="bg-navy border-tan-dark text-tan pr-10"
                  disabled={isSubmitting}
                />
                
                {isValid !== null && (
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    {isValid ? (
                      <CheckCircle className="text-green-400" size={16} />
                    ) : (
                      <AlertCircle className="text-red-400" size={16} />
                    )}
                  </div>
                )}
              </div>
              
              {isValid === false && (
                <p className="text-red-400 text-xs">
                  Please enter a valid Solana wallet address (32-44 characters)
                </p>
              )}
            </div>
            
            <div className="flex gap-2">
              <Button
                onClick={handlePaste}
                variant="outline"
                size="sm"
                disabled={isSubmitting}
                className="border-tan text-tan hover:bg-tan hover:text-navy"
              >
                Paste
              </Button>
              
              <Button
                onClick={handleSubmit}
                disabled={!isValid || isSubmitting}
                className="flex-1 bg-tan text-navy hover:bg-tan-dark"
              >
                {isSubmitting ? 'Processing...' : 'Continue with This Address'}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="text-center">
        <p className="text-xs text-tan-dark">
          Make sure you own this wallet address. Your BroCode will be linked to it permanently.
        </p>
      </div>
    </div>
  );
};

export default InitiationWallet;
